import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { carriers } from '@/api/carriers';
import type { CarrierRow, CarrierScope, CarrierKind, SipConfig } from '@/api/carriers';
import { customers } from '@/api/customers';
import { resellers } from '@/api/resellers';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { CidrTagInput } from './CidrTagInput';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/cn';

// ──────────────────────────────────────────────────────────
// Types / constants
// ──────────────────────────────────────────────────────────

const PROVIDERS = [
  { value: 'telnyx', label: 'Telnyx' },
  { value: 'signalwire', label: 'SignalWire' },
  { value: 'bandwidth', label: 'Bandwidth' },
  { value: 'twilio', label: 'Twilio' },
  { value: 'voxbone', label: 'Voxbone' },
  { value: 'generic_sip', label: 'Generic SIP' },
] as const;

interface FormState {
  // Identity
  name: string;
  provider: string;
  kind: CarrierKind;
  scope: CarrierScope;
  reseller_id: string;
  customer_id: string;
  // SIP connection
  host: string;
  port: string;
  transport: 'udp' | 'tcp';
  proxy: string;
  from_user: string;
  from_domain: string;
  // Auth
  register: boolean;
  username: string;
  password: string;
  clearPassword: boolean;
  // Inbound allowlist
  inbound_cidrs: string[];
  // Advanced
  expire_seconds: string;
  priority: string;
  caller_id_in_from: boolean;
}

function defaultForm(row?: CarrierRow): FormState {
  if (row) {
    const s = row.sip_config;
    return {
      name: row.name,
      provider: row.provider,
      kind: row.kind,
      scope: row.scope,
      reseller_id: row.reseller_id ?? '',
      customer_id: row.customer_id ?? '',
      host: s.host,
      port: String(s.port ?? 5060),
      transport: s.transport ?? 'udp',
      proxy: s.proxy ?? '',
      from_user: s.from_user ?? '',
      from_domain: s.from_domain ?? '',
      register: s.register ?? false,
      username: s.username ?? '',
      password: '',   // never pre-fill; edit mode shows write-only hint
      clearPassword: false,
      inbound_cidrs: s.inbound_cidrs ?? [],
      expire_seconds: String(s.expire_seconds ?? 3600),
      priority: String(row.priority),
      caller_id_in_from: s.caller_id_in_from ?? true,
    };
  }
  return {
    name: '',
    provider: 'telnyx',
    kind: 'voice',
    scope: 'customer',
    reseller_id: '',
    customer_id: '',
    host: '',
    port: '5060',
    transport: 'udp',
    proxy: '',
    from_user: '',
    from_domain: '',
    register: true,
    username: '',
    password: '',
    clearPassword: false,
    inbound_cidrs: [],
    expire_seconds: '3600',
    priority: '100',
    caller_id_in_from: true,
  };
}

// ──────────────────────────────────────────────────────────
// Section divider
// ──────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="mb-3 mt-5 first:mt-0">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
        {label}
      </p>
      <div className="mt-1 h-px bg-border" />
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────

interface Props {
  mode: 'create' | 'edit';
  row?: CarrierRow;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

// ──────────────────────────────────────────────────────────
// Dialog
// ──────────────────────────────────────────────────────────

export function CarrierDialog({ mode, row, open, onOpenChange }: Props) {
  const user = useCurrentUser();
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(() => defaultForm(row));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [apiError, setApiError] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Derive allowed scopes from role
  const scopeOptions = (() => {
    if (user.is_super_admin) return ['platform', 'reseller', 'customer'] as CarrierScope[];
    if (user.role === 'reseller_owner') return ['reseller', 'customer'] as CarrierScope[];
    return ['customer'] as CarrierScope[];
  })();
  const scopeLocked = scopeOptions.length === 1; // office_manager forced to customer

  // Reset when dialog opens / row changes
  useEffect(() => {
    if (open) {
      setForm(defaultForm(row));
      setErrors({});
      setApiError('');
      setAdvancedOpen(false);
    }
  }, [open, row]);

  // ── Pickers for reseller / customer comboboxes ──
  const needsResellerPicker = user.is_super_admin && form.scope === 'reseller';
  const needsCustomerPicker =
    (user.is_super_admin || user.role === 'reseller_owner') && form.scope === 'customer';

  const { data: resellerList } = useQuery({
    queryKey: ['resellers', 'picker'],
    queryFn: () => resellers.list({ limit: 200 }),
    enabled: open && needsResellerPicker,
  });

  const { data: customerList } = useQuery({
    queryKey: ['customers', 'picker'],
    queryFn: () => customers.list({ limit: 200 }),
    enabled: open && needsCustomerPicker,
  });

  // ── Mutations ──────────────────────────────────

  const save = useMutation({
    mutationFn: () => {
      const sipConfig: SipConfig = {
        host: form.host.trim(),
        port: form.port ? Number(form.port) : undefined,
        transport: form.transport,
        proxy: form.proxy.trim() || null,
        from_user: form.from_user.trim() || null,
        from_domain: form.from_domain.trim() || null,
        register: form.register,
        username: form.register ? form.username.trim() : undefined,
        expire_seconds: Number(form.expire_seconds),
        caller_id_in_from: form.caller_id_in_from,
        inbound_cidrs: form.inbound_cidrs.length > 0 ? form.inbound_cidrs : undefined,
      };

      if (mode === 'create') {
        return carriers.create({
          name: form.name.trim(),
          kind: form.kind,
          provider: form.provider,
          scope: form.scope,
          reseller_id: form.reseller_id || null,
          customer_id: form.customer_id || null,
          sip_config: sipConfig,
          priority: Number(form.priority),
          password: form.password || undefined,
        });
      } else {
        // Build patch: only include password fields when user touched them
        const body: Parameters<typeof carriers.update>[1] = {
          name: form.name.trim(),
          kind: form.kind,
          provider: form.provider,
          sip_config: sipConfig,
          priority: Number(form.priority),
          active: row?.active,
        };
        if (user.is_super_admin) {
          body.scope = form.scope;
        }
        if (form.clearPassword) {
          body.password = '';     // explicit clear
        } else if (form.password) {
          body.password = form.password; // change
        }
        // else: password absent → keep existing
        return carriers.update(row!.id, body);
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['carriers'] });
      qc.invalidateQueries({ queryKey: ['freeswitch', 'status'] });
      toast({
        title: mode === 'create' ? 'Carrier added' : 'Carrier updated',
        description: `"${data.name}" saved and synced to FreeSWITCH.`,
      });
      if (data.sync_warning) {
        toast({
          title: 'FreeSWITCH sync warning',
          description: `Saved, but FreeSWITCH sync reported: ${data.sync_warning}. Click "Sync to FreeSWITCH" in the list to retry.`,
          variant: 'destructive',
        });
      }
      onOpenChange(false);
    },
    onError: (err) => {
      setApiError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    },
  });

  // ── Validation ─────────────────────────────────

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (form.name.trim().length > 64) errs.name = 'Name must be 64 characters or fewer.';
    if (!form.host.trim()) errs.host = 'Host is required.';
    if (form.port && (isNaN(Number(form.port)) || Number(form.port) < 1 || Number(form.port) > 65535)) {
      errs.port = 'Port must be 1–65535.';
    }
    if (form.register && !form.username.trim()) {
      errs.username = 'Username is required when registration is enabled.';
    }
    if (needsResellerPicker && !form.reseller_id) {
      errs.reseller_id = 'Select a reseller.';
    }
    if (needsCustomerPicker && !form.customer_id) {
      errs.customer_id = 'Select a customer.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;
    save.mutate();
  }

  // ──────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!save.isPending) onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add carrier' : 'Edit carrier'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-1">

          {/* ── Section 1: Identity ──────────────────────── */}
          <SectionLabel label="Identity" />

          <div className="space-y-3">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="carr-name">
                Name <span aria-hidden className="text-red-500">*</span>
              </Label>
              <Input
                id="carr-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Telnyx main trunk"
                maxLength={64}
                autoFocus={mode === 'create'}
              />
              {errors.name && <p role="alert" className="text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Provider + Kind — side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="carr-provider">Provider</Label>
                <Select value={form.provider} onValueChange={(v) => set('provider', v)}>
                  <SelectTrigger id="carr-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="carr-kind">Kind</Label>
                <Select value={form.kind} onValueChange={(v) => set('kind', v as CarrierKind)}>
                  <SelectTrigger id="carr-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voice">Voice</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Scope */}
            <div className="space-y-1.5">
              <Label htmlFor="carr-scope">Scope</Label>
              {scopeLocked ? (
                <Input id="carr-scope" value="Customer" disabled className="bg-muted/40" />
              ) : (
                <Select value={form.scope} onValueChange={(v) => { set('scope', v as CarrierScope); set('reseller_id', ''); set('customer_id', ''); }}>
                  <SelectTrigger id="carr-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeOptions.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Reseller picker — super_admin + scope=reseller */}
            {needsResellerPicker && (
              <div className="space-y-1.5">
                <Label htmlFor="carr-reseller">
                  Reseller <span aria-hidden className="text-red-500">*</span>
                </Label>
                <Select value={form.reseller_id} onValueChange={(v) => set('reseller_id', v)}>
                  <SelectTrigger id="carr-reseller">
                    <SelectValue placeholder="Select reseller…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(resellerList?.rows ?? []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.reseller_id && <p role="alert" className="text-xs text-red-500">{errors.reseller_id}</p>}
              </div>
            )}

            {/* Customer picker — super_admin/reseller_owner + scope=customer */}
            {needsCustomerPicker && (
              <div className="space-y-1.5">
                <Label htmlFor="carr-customer">
                  Customer <span aria-hidden className="text-red-500">*</span>
                </Label>
                <Select value={form.customer_id} onValueChange={(v) => set('customer_id', v)}>
                  <SelectTrigger id="carr-customer">
                    <SelectValue placeholder="Select customer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(customerList?.rows ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.customer_id && <p role="alert" className="text-xs text-red-500">{errors.customer_id}</p>}
              </div>
            )}
          </div>

          {/* ── Section 2: SIP connection ─────────────────── */}
          <SectionLabel label="SIP connection" />

          <div className="space-y-3">
            {/* Host */}
            <div className="space-y-1.5">
              <Label htmlFor="carr-host">
                Host <span aria-hidden className="text-red-500">*</span>
              </Label>
              <Input
                id="carr-host"
                value={form.host}
                onChange={(e) => set('host', e.target.value)}
                placeholder="sip.telnyx.com"
                className="font-mono"
                maxLength={200}
              />
              {errors.host && <p role="alert" className="text-xs text-red-500">{errors.host}</p>}
            </div>

            {/* Port + Transport — side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="carr-port">Port</Label>
                <Input
                  id="carr-port"
                  type="number"
                  min={1}
                  max={65535}
                  value={form.port}
                  onChange={(e) => set('port', e.target.value)}
                  className="font-mono"
                />
                {errors.port && <p role="alert" className="text-xs text-red-500">{errors.port}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="carr-transport">Transport</Label>
                <Select value={form.transport} onValueChange={(v) => set('transport', v as 'udp' | 'tcp')}>
                  <SelectTrigger id="carr-transport">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="udp">UDP</SelectItem>
                    <SelectItem value="tcp">TCP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Proxy */}
            <div className="space-y-1.5">
              <Label htmlFor="carr-proxy">
                Proxy <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="carr-proxy"
                value={form.proxy}
                onChange={(e) => set('proxy', e.target.value)}
                placeholder="proxy.carrier.com:5060"
                className="font-mono"
                maxLength={200}
              />
            </div>

            {/* From user + From domain — side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="carr-from-user">
                  From user <span className="text-xs text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="carr-from-user"
                  value={form.from_user}
                  onChange={(e) => set('from_user', e.target.value)}
                  className="font-mono"
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="carr-from-domain">
                  From domain <span className="text-xs text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="carr-from-domain"
                  value={form.from_domain}
                  onChange={(e) => set('from_domain', e.target.value)}
                  className="font-mono"
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          {/* ── Section 3: Authentication ─────────────────── */}
          <SectionLabel label="Authentication" />

          <div className="space-y-3">
            {/* Register toggle */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="carr-register"
                checked={form.register}
                onCheckedChange={(v) => set('register', !!v)}
              />
              <Label htmlFor="carr-register" className="font-normal cursor-pointer">
                Register to this carrier (SIP REGISTER auth)
              </Label>
            </div>

            {form.register && (
              <>
                {/* Username */}
                <div className="space-y-1.5">
                  <Label htmlFor="carr-username">
                    Username <span aria-hidden className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="carr-username"
                    value={form.username}
                    onChange={(e) => set('username', e.target.value)}
                    placeholder="trunk-username"
                    className="font-mono"
                    maxLength={200}
                    autoComplete="off"
                  />
                  {errors.username && <p role="alert" className="text-xs text-red-500">{errors.username}</p>}
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <Label htmlFor="carr-password">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      id="carr-password"
                      type="password"
                      value={form.password}
                      onChange={(e) => { set('password', e.target.value); set('clearPassword', false); }}
                      placeholder={mode === 'edit' ? '(leave blank to keep current)' : 'SIP auth password'}
                      className="font-mono flex-1"
                      maxLength={500}
                      autoComplete="new-password"
                      disabled={form.clearPassword}
                    />
                    {mode === 'edit' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(
                          'shrink-0 text-xs',
                          form.clearPassword && 'border-red-400 text-red-600 hover:bg-red-50',
                        )}
                        onClick={() => {
                          set('clearPassword', !form.clearPassword);
                          set('password', '');
                        }}
                      >
                        {form.clearPassword ? 'Undo clear' : 'Clear saved password'}
                      </Button>
                    )}
                  </div>
                  {mode === 'edit' && (
                    <p className="text-xs text-muted-foreground">
                      {form.clearPassword
                        ? 'The saved password will be cleared on save.'
                        : row?.has_password
                          ? 'A password is saved. Leave blank to keep it, or type to change.'
                          : 'No password saved.'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── Section 4: Inbound allowlist ──────────────── */}
          <SectionLabel label="Inbound allowlist" />

          <div className="space-y-1.5">
            <Label htmlFor="carr-cidrs">
              Allowed CIDR blocks <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <CidrTagInput
              value={form.inbound_cidrs}
              onChange={(v) => set('inbound_cidrs', v)}
              describedBy="carr-cidrs-help"
            />
            <p id="carr-cidrs-help" className="text-xs text-muted-foreground">
              Required for IP-auth trunks (most US carriers). Leave empty if your carrier uses registration auth only.
              Type a CIDR and press Enter or comma to add.
            </p>
          </div>

          {/* ── Section 5: Advanced (collapsed) ──────────── */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="mt-5 flex w-full items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            <div className="h-px flex-1 bg-border" />
            Advanced
            {advancedOpen ? <ChevronUp className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
            <div className="h-px flex-1 bg-border" />
          </button>

          {advancedOpen && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="carr-expire">Expire seconds</Label>
                  <Input
                    id="carr-expire"
                    type="number"
                    min={60}
                    max={86400}
                    value={form.expire_seconds}
                    onChange={(e) => set('expire_seconds', e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="carr-priority">Priority</Label>
                  <Input
                    id="carr-priority"
                    type="number"
                    min={0}
                    max={9999}
                    value={form.priority}
                    onChange={(e) => set('priority', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Lower = higher priority.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="carr-callerid"
                  checked={form.caller_id_in_from}
                  onCheckedChange={(v) => set('caller_id_in_from', v)}
                />
                <Label htmlFor="carr-callerid" className="font-normal cursor-pointer">
                  Send caller-ID in SIP From header
                </Label>
              </div>
            </div>
          )}

          {/* ── API-level error ───────────────────────────── */}
          {apiError && (
            <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {apiError}
            </div>
          )}

          {/* ── Footer ───────────────────────────────────── */}
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={save.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? mode === 'create' ? 'Adding…' : 'Saving…'
                : mode === 'create' ? 'Add carrier' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
